import React, { useEffect, useRef, useState } from 'react'
import { Select as SutraSelect } from 'sutra/components/Select/Select'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import {
  NumericOperatorSelectWrap,
  NumericValueInput,
  PaneDivider,
  PaneExclusiveGroup,
  PaneEyebrow,
  PaneOrDivider,
  PaneRow,
  ScrollablePaneContainer,
} from 'features/charts-v2/components/filter-popover-pane.styles'
import { FilterPopoverPaneFooter } from 'features/charts-v2/components/FilterPopoverPaneFooter/FilterPopoverPaneFooter'
import {
  resolveVisibleSentimentValues,
  type SentimentPaneRestriction,
} from 'features/charts-v2/components/FilterPopoverSentimentPane/sentiment-pane-restriction'
import { type SentimentMode } from 'features/charts-v2/components/SentimentFilterCard'
import { SentimentTypeSelector } from 'features/charts-v2/components/SentimentTypeSelector/SentimentTypeSelector'
import { SentimentValueRow } from 'features/charts-v2/components/SentimentValueRow/SentimentValueRow'
import {
  NUMERIC_OPERATOR_OPTIONS,
  type NumericOperatorType,
} from 'features/charts-v2/types'
import type {
  FilterValueSortBy,
  FilterValueSortDirection,
} from 'features/charts-v2/utils/sort-filter-option-values'
import { FilterOptionSelect } from 'features/filters/types'

export type { SentimentPaneRestriction }

export interface SentimentCommitted {
  mode: SentimentMode
  values: string[]
  numericOperator: NumericOperatorType
  numericValue: number | undefined
  hasCommittedFilter: boolean
}

interface FilterPopoverSentimentPaneProps {
  sentimentFilterOption: FilterOptionSelect | undefined
  sortOptions: {
    sortBy: FilterValueSortBy
    sortDirection: FilterValueSortDirection
  }
  committed: SentimentCommitted
  onApply: (draft: {
    mode: SentimentMode
    values: string[]
    numericOperator: NumericOperatorType
    numericValue: number | undefined
  }) => void
  onClear: () => void
  restriction?: SentimentPaneRestriction
}

// In-popover editor for the Sentiment filter. Like every other pane, nothing
// writes to the panel until Apply — the type radios, the categorical
// checkboxes/"only" buttons, and the avg-sentiment operator+value row all
// edit draft state only. Unlike Rating (one write on Apply), Sentiment's
// committed state spans three backend fields (sentiment / post_sentiment /
// sentiment_filter); the caller's `onApply` resolves which of
// {categorical, numeric, mode-only} the draft represents.
export const FilterPopoverSentimentPane: React.FC<
  FilterPopoverSentimentPaneProps
> = ({
  sentimentFilterOption,
  sortOptions,
  committed,
  onApply,
  onClear,
  restriction,
}) => {
  const [draftMode, setDraftMode] = useState(committed.mode)
  const [draftValues, setDraftValues] = useState(committed.values)
  const [draftNumericOperator, setDraftNumericOperator] = useState(
    committed.numericOperator,
  )
  const [draftNumericValue, setDraftNumericValue] = useState(
    committed.numericValue,
  )

  // `committed` is a fresh object (and `values` a fresh array) every render
  // of the parent pane, but its content only changes on a genuine external
  // commit. Mirror it into a ref and re-sync only when its serialized
  // content actually changes — the same guard FilterPopoverKeywordsPane uses
  // for its own array-shaped committed state.
  const committedRef = useRef(committed)
  committedRef.current = committed
  const committedKey = JSON.stringify(committed)
  useEffect(() => {
    const next = committedRef.current
    setDraftMode(next.mode)
    setDraftValues(next.values)
    setDraftNumericOperator(next.numericOperator)
    setDraftNumericValue(next.numericValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- committedRef is a stable ref-backed mirror, not a reactive dependency; only committedKey should re-trigger this sync
  }, [committedKey])

  const hasCategorical = draftValues.length > 0
  const hasNumeric = draftNumericValue !== undefined

  const isDirty =
    draftMode !== committed.mode ||
    JSON.stringify(draftValues) !== JSON.stringify(committed.values) ||
    draftNumericOperator !== committed.numericOperator ||
    draftNumericValue !== committed.numericValue

  const handleToggleValue = (value: string): void => {
    if (hasNumeric) return
    setDraftValues((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    )
  }

  const handleOnly = (value: string): void => {
    if (hasNumeric) return
    setDraftValues([value])
  }

  const sortedValues = resolveVisibleSentimentValues(
    sentimentFilterOption?.values ?? [],
    draftMode,
    draftValues,
    sortOptions,
    restriction,
  )

  return (
    <ScrollablePaneContainer>
      <PaneEyebrow>Type</PaneEyebrow>
      <SentimentTypeSelector
        value={draftMode}
        onChange={setDraftMode}
        allowedModes={restriction?.allowedModes}
      />

      <PaneDivider />
      <PaneEyebrow>Values</PaneEyebrow>
      <Tooltip
        content={
          hasNumeric ? 'Can only select one value type at a time' : undefined
        }
        zIndex={2001}
      >
        <PaneExclusiveGroup $disabled={hasNumeric}>
          {sortedValues.map((value) => (
            <SentimentValueRow
              key={value.value}
              value={value}
              isChecked={draftValues.includes(value.value)}
              onToggle={() => handleToggleValue(value.value)}
              onOnly={() => handleOnly(value.value)}
            />
          ))}
        </PaneExclusiveGroup>
      </Tooltip>

      <PaneOrDivider>OR</PaneOrDivider>

      <Tooltip
        content={
          hasCategorical
            ? 'Can only select one value type at a time'
            : undefined
        }
        zIndex={2001}
      >
        <PaneExclusiveGroup $disabled={hasCategorical}>
          <PaneRow>
            <NumericOperatorSelectWrap>
              <SutraSelect
                value={draftNumericOperator}
                disabled={hasCategorical}
                onChange={(picked) => setDraftNumericOperator(picked)}
                options={NUMERIC_OPERATOR_OPTIONS}
                zIndex={2001}
              />
            </NumericOperatorSelectWrap>
            <NumericValueInput
              type="number"
              $height="30px"
              value={draftNumericValue ?? ''}
              disabled={hasCategorical}
              onChange={(e) =>
                setDraftNumericValue(
                  e.target.value === '' ? undefined : Number(e.target.value),
                )
              }
              min={0}
              max={1}
              step={0.1}
              placeholder="e.g. 0.3"
            />
          </PaneRow>
        </PaneExclusiveGroup>
      </Tooltip>

      <FilterPopoverPaneFooter
        applyDisabled={!isDirty}
        clearDisabled={!committed.hasCommittedFilter}
        onApply={() =>
          onApply({
            mode: draftMode,
            values: draftValues,
            numericOperator: draftNumericOperator,
            numericValue: draftNumericValue,
          })
        }
        onClear={onClear}
      />
    </ScrollablePaneContainer>
  )
}
